import { useEffect, useMemo, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Snowflake, MessageCircle, RefreshCw, CheckCircle2, ArrowRight, XCircle, Filter, Download, Zap, QrCode, Trash2, Save, TrendingUp, Play, Pause, X } from "lucide-react";
import { toast } from "sonner";
import UniversalLeadAdd from "@/components/app/UniversalLeadAdd";

const STATUS_STYLES = {
  not_contacted: "bg-slate-800 text-slate-300 border-slate-700",
  whatsapp_sent: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  replied_yes: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  replied_no: "bg-red-500/20 text-red-300 border-red-500/40",
  invalid: "bg-red-500/20 text-red-300 border-red-500/40",
  converted: "bg-amber-500/20 text-amber-300 border-amber-500/40",
};
const TYPE_STYLES = {
  excel: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  ocr: "bg-sky-500/20 text-sky-300 border-sky-500/40",
  contacts: "bg-violet-500/20 text-violet-300 border-violet-500/40",
};
const ROLE_LABELS = {
  admin: "Admin", bank_staff: "Bank Staff", telecaller: "Telecaller",
  field_staff: "Field Staff", mahalir_manager: "Mahalir Kuzhu Manager",
  office_staff: "Office Staff", customer: "Customer",
};

export default function SourcesPoolPage() {
  const [data, setData] = useState({ rows: [], counts: {}, source_lists: [] });
  const [selected, setSelected] = useState({});
  const [status, setStatus] = useState("all");
  const [typeF, setTypeF] = useState("all");
  const [q, setQ] = useState("");
  const [sourceQ, setSourceQ] = useState("");
  const [showBlast, setShowBlast] = useState(false);
  const [blastMsg, setBlastMsg] = useState("Hi {name}, this is Shakthi Consultancy. We help you with quick loan approvals. Reply YES via {link} to know more.");
  const [busy, setBusy] = useState(false);
  const [wa, setWa] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [showQR, setShowQR] = useState(null);
  const [replyRate, setReplyRate] = useState(null);
  const [blastProgress, setBlastProgress] = useState(null);  // { sent, total, paused }
  const [pauseRef] = useState({ current: false });

  const load = async () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (typeF !== "all") params.set("source_type", typeF);
    if (q) params.set("q", q);
    if (sourceQ) params.set("source_name", sourceQ);
    try {
      const { data } = await api.get("/admin/sources-pool?" + params.toString());
      setData(data);
    } catch (e) { toast.error(errorMessage(e)); }
    try { const r = await api.get("/whatsapp/today"); setWa(r.data); } catch {}
    try { const r = await api.get("/admin/blast-templates"); setTemplates(r.data); } catch {}
    try { const r = await api.get("/admin/pool-reply-rate"); setReplyRate(r.data); } catch {}
  };
  useEffect(() => { load(); }, [status, typeF]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, sourceQ]);

  const selectedIds = useMemo(() => Object.keys(selected).filter(k => selected[k]), [selected]);
  const toggleAll = () => {
    if (selectedIds.length === data.rows.length) setSelected({});
    else setSelected(Object.fromEntries(data.rows.map(r => [r.id, true])));
  };

  const blast = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one number");
    setBusy(true);
    pauseRef.current = false;
    try {
      const chunkSize = 10;
      setBlastProgress({ sent: 0, total: selectedIds.length, paused: false });
      let totalSent = 0;
      for (let i = 0; i < selectedIds.length; i += chunkSize) {
        // Pause loop if user hit pause
        while (pauseRef.current) {
          setBlastProgress(p => p && { ...p, paused: true });
          await new Promise(r => setTimeout(r, 300));
        }
        setBlastProgress(p => p && { ...p, paused: false });
        const chunk = selectedIds.slice(i, i + chunkSize);
        const { data: res } = await api.post("/admin/sources-pool/whatsapp-blast", {
          pool_ids: chunk, message: blastMsg,
        });
        // Open wa.me links for this chunk
        res.results.forEach(r => window.open(r.wa_url, "_blank"));
        totalSent += res.sent;
        setBlastProgress({ sent: totalSent, total: selectedIds.length, paused: false });
        // Small breath between chunks
        await new Promise(r => setTimeout(r, 200));
      }
      toast.success(`${totalSent} sends queued`);
      setSelected({});
      setShowBlast(false);
      setBlastProgress(null);
      load();
    } catch (e) { toast.error(errorMessage(e)); setBlastProgress(null); }
    setBusy(false);
  };

  const saveTemplate = async () => {
    const name = window.prompt("Template name?", "");
    if (!name || !name.trim()) return;
    try {
      await api.post("/admin/blast-templates", { name: name.trim(), message: blastMsg });
      const r = await api.get("/admin/blast-templates"); setTemplates(r.data);
      toast.success("Template saved");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/admin/blast-templates/${id}`);
      const r = await api.get("/admin/blast-templates"); setTemplates(r.data);
      toast.success("Template deleted");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const convert = async (poolId) => {
    try {
      await api.post(`/admin/sources-pool/${poolId}/convert`);
      toast.success("Converted to Lead");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const bulkConvert = async () => {
    if (selectedIds.length === 0) return toast.error("Select at least one number");
    if (!window.confirm(`Convert ${selectedIds.length} selected numbers into HOT leads?`)) return;
    setBusy(true);
    try {
      const { data: res } = await api.post("/admin/sources-pool/convert-bulk", { pool_ids: selectedIds });
      toast.success(`${res.converted} converted · ${res.failed} skipped`);
      setSelected({});
      load();
    } catch (e) { toast.error(errorMessage(e)); }
    setBusy(false);
  };

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (status !== "all") params.set("status", status);
    if (typeF !== "all") params.set("source_type", typeF);
    if (q) params.set("q", q);
    if (sourceQ) params.set("source_name", sourceQ);
    try {
      const res = await api.get("/admin/sources-pool/export.csv?" + params.toString(), { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `source_pool_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const setEntryStatus = async (poolId, newStatus) => {
    try {
      await api.post(`/admin/sources-pool/${poolId}/status`, { status: newStatus });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="sources-pool-page">
      <div className="card-surface p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading text-xl text-white flex items-center gap-2">
              <Snowflake className="w-5 h-5 text-sky-400"/> Source Numbers — Cold Pool
            </div>
            <div className="text-sm text-slate-500 mt-1">
              Raw contacts from Excel / OCR / Phone Contacts uploads. These are NOT leads. They become leads only after replying YES.
            </div>
          </div>
          {wa && <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1.5 text-xs">
            <MessageCircle className="inline w-3.5 h-3.5 mr-1 text-emerald-400"/>
            <span className="text-emerald-300 font-bold">{wa.count}/{wa.limit}</span> WhatsApp today
          </div>}
        </div>
      </div>

      {/* Upload New Source Pool */}
      <UniversalLeadAdd/>

      {/* Reply-rate widget */}
      {replyRate && replyRate.rows.length > 0 && (
        <div className="card-surface p-6" data-testid="reply-rate-widget">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div className="font-heading text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400"/> Reply-Rate by Source List
            </div>
            <div className="text-xs text-slate-500">
              Overall conversion: <span className="text-emerald-400 font-bold">{replyRate.totals.overall_conversion_pct}%</span>
              {" · "}{replyRate.totals.total_converted}/{replyRate.totals.total_numbers} converted
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-64 overflow-y-auto">
            {replyRate.rows.slice(0, 12).map(r => (
              <div key={r.source_name} className="bg-slate-950/60 border border-slate-800 rounded-lg p-3">
                <div className="text-sm text-white font-bold truncate">{r.source_name}</div>
                <div className="flex gap-3 mt-2 text-xs">
                  <span className="text-slate-400">{r.total} total</span>
                  <span className="text-sky-400">{r.sent} sent</span>
                  <span className="text-amber-400">{r.converted} converted</span>
                </div>
                <div className="mt-2 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: `${r.conversion_rate_pct}%` }}/>
                </div>
                <div className="text-[10px] text-emerald-400 mt-1">{r.conversion_rate_pct}% converted</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Counts */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { k: "not_contacted", label: "Not Contacted", color: "text-slate-300" },
          { k: "whatsapp_sent", label: "WA Sent", color: "text-sky-400" },
          { k: "replied_yes", label: "Replied YES", color: "text-emerald-400" },
          { k: "replied_no", label: "Replied NO", color: "text-red-400" },
          { k: "converted", label: "Converted", color: "text-amber-400" },
          { k: "invalid", label: "Invalid", color: "text-red-400" },
        ].map(c => (
          <button key={c.k} onClick={() => setStatus(status === c.k ? "all" : c.k)}
            data-testid={`pool-count-${c.k}`}
            className={`card-surface p-4 text-left transition-transform ${status === c.k ? "scale-[1.02] border-amber-500/40" : ""}`}>
            <div className={`font-display text-2xl ${c.color}`}>{data.counts?.[c.k] || 0}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{c.label}</div>
          </button>
        ))}
      </div>

      {/* Filters + Blast */}
      <div className="card-surface p-6 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-wrap gap-2 flex-1">
            <Input data-testid="pool-search" placeholder="Search phone / name" value={q} onChange={e => setQ(e.target.value)} className="bg-slate-950 border-slate-700 max-w-xs"/>
            <Input data-testid="pool-source-search" placeholder="Enter Source Name" value={sourceQ} onChange={e => setSourceQ(e.target.value)} className="bg-slate-950 border-slate-700 max-w-xs"/>
            <select data-testid="pool-type-filter" value={typeF} onChange={e => setTypeF(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 text-sm">
              <option value="all">All Types</option>
              <option value="excel">Excel</option>
              <option value="ocr">OCR</option>
              <option value="contacts">Contacts</option>
            </select>
            <select data-testid="pool-status-filter" value={status} onChange={e => setStatus(e.target.value)} className="bg-slate-950 border border-slate-700 text-white rounded-md px-3 text-sm">
              <option value="all">All Status</option>
              <option value="not_contacted">Not Contacted</option>
              <option value="whatsapp_sent">WhatsApp Sent</option>
              <option value="replied_yes">Replied YES</option>
              <option value="replied_no">Replied NO</option>
              <option value="converted">Converted</option>
              <option value="invalid">Invalid</option>
            </select>
            <Button variant="secondary" onClick={load} data-testid="pool-refresh" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
              <RefreshCw className="w-4 h-4"/>
            </Button>
          </div>
          <Button
            data-testid="pool-blast-btn"
            onClick={() => setShowBlast(true)}
            disabled={selectedIds.length === 0}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full">
            <MessageCircle className="w-4 h-4 mr-2"/>Send Loan Ad ({selectedIds.length})
          </Button>
          <Button
            data-testid="pool-bulk-convert"
            onClick={bulkConvert}
            disabled={selectedIds.length === 0 || busy}
            className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold rounded-full">
            <Zap className="w-4 h-4 mr-2"/>Convert to Leads ({selectedIds.length})
          </Button>
          <Button
            data-testid="pool-export-csv"
            onClick={exportCsv}
            variant="secondary"
            className="bg-slate-800 hover:bg-slate-700 text-white rounded-full">
            <Download className="w-4 h-4 mr-2"/>Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="card-surface p-6">
        <div className="text-sm text-slate-500 mb-3">Showing {data.rows.length} numbers</div>
        <Table>
          <TableHeader><TableRow className="border-white/5">
            <TableHead className="w-10">
              <input type="checkbox" checked={selectedIds.length > 0 && selectedIds.length === data.rows.length} onChange={toggleAll} data-testid="pool-select-all"/>
            </TableHead>
            <TableHead>Phone</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Source Name</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Uploaded By</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>WhatsApp</TableHead>
            <TableHead>Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.rows.map(r => (
              <TableRow key={r.id} className="border-white/5" data-testid={`pool-row-${r.id}`}>
                <TableCell>
                  <input type="checkbox" checked={!!selected[r.id]}
                    onChange={e => setSelected({ ...selected, [r.id]: e.target.checked })}
                    disabled={r.status === "converted"} data-testid={`pool-check-${r.id}`}/>
                </TableCell>
                <TableCell className="font-mono-alt">{r.phone}</TableCell>
                <TableCell className="text-white">{r.name || <span className="text-slate-600">—</span>}</TableCell>
                <TableCell className="text-slate-300 text-sm" data-testid={`pool-source-${r.id}`}>{r.source_name}</TableCell>
                <TableCell><Badge className={TYPE_STYLES[r.source_type] + " uppercase text-[10px] tracking-widest"}>{r.source_type}</Badge></TableCell>
                <TableCell className="text-sm">
                  <div className="text-white">{r.uploaded_by_name || "—"}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-widest">{ROLE_LABELS[r.uploaded_by_role] || r.uploaded_by_role}</div>
                  <div className="text-[10px] text-slate-500">{fmtDate(r.uploaded_date)}</div>
                </TableCell>
                <TableCell><Badge className={STATUS_STYLES[r.status]} data-testid={`pool-status-${r.id}`}>{r.status.replace(/_/g," ")}</Badge></TableCell>
                <TableCell className="text-xs">
                  {r.whatsapp_sent ? (
                    <div className="text-emerald-400">Sent<div className="text-[10px] text-slate-500">{fmtDate(r.whatsapp_sent_at)}</div></div>
                  ) : <span className="text-slate-600">Not sent</span>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1">
                    {r.status === "converted" ? (
                      <span className="text-amber-400 text-xs">→ {r.converted_lead_id}</span>
                    ) : (
                      <>
                        <Button size="sm" onClick={() => convert(r.id)} data-testid={`convert-${r.id}`} className="bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs rounded-full h-7">
                          <ArrowRight className="w-3 h-3 mr-1"/>Convert
                        </Button>
                        {r.status !== "invalid" && (
                          <button onClick={() => setEntryStatus(r.id, "invalid")} className="text-[10px] text-red-400 hover:underline">Mark invalid</button>
                        )}
                      </>
                    )}
                    <button onClick={() => setShowQR(r)} data-testid={`qr-${r.id}`} className="text-[10px] text-sky-400 hover:underline flex items-center gap-1 justify-center">
                      <QrCode className="w-3 h-3"/> QR Code
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {data.rows.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No numbers in this filter</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Blast dialog */}
      <Dialog open={showBlast} onOpenChange={setShowBlast}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="blast-dialog">
          <DialogHeader><DialogTitle>Send Loan Ad via WhatsApp</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-slate-400">Sending to <span className="text-white font-bold">{selectedIds.length}</span> numbers · {wa && `${wa.remaining}/${wa.limit} remaining today`}</div>
            {templates.length > 0 && (
              <div>
                <Label className="text-slate-400 text-xs uppercase tracking-widest">Load a saved template</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {templates.map(t => (
                    <div key={t.id} className="group flex items-center bg-slate-950 border border-slate-700 rounded-full pl-3 pr-1 py-1">
                      <button data-testid={`tpl-${t.id}`} onClick={() => setBlastMsg(t.message)} className="text-xs text-white hover:text-amber-400">{t.name}</button>
                      <button onClick={() => deleteTemplate(t.id)} className="ml-2 opacity-40 hover:opacity-100 text-red-400"><X className="w-3 h-3"/></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <Label className="text-slate-400 text-xs uppercase tracking-widest">Message</Label>
              <Textarea data-testid="blast-msg" value={blastMsg} onChange={e => setBlastMsg(e.target.value)} className="bg-slate-950 border-slate-700 min-h-[120px] mt-2"/>
              <div className="text-[10px] text-slate-500 mt-1">
                Placeholders: <code className="text-amber-400">{'{name}'}</code> = customer name, <code className="text-amber-400">{'{link}'}</code> = per-customer reply URL (auto-appended if missing)
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="secondary" onClick={saveTemplate} data-testid="save-template-btn" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full text-xs">
                <Save className="w-3 h-3 mr-1"/>Save as Template
              </Button>
            </div>
            {blastProgress && (
              <div className="bg-slate-950/60 border border-slate-800 rounded-lg p-3" data-testid="blast-progress">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-slate-400">Progress: {blastProgress.sent}/{blastProgress.total}</span>
                  <button onClick={() => pauseRef.current = !pauseRef.current} data-testid="blast-pause" className="text-amber-400 hover:underline flex items-center gap-1">
                    {blastProgress.paused ? <><Play className="w-3 h-3"/>Resume</> : <><Pause className="w-3 h-3"/>Pause</>}
                  </button>
                </div>
                <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(blastProgress.sent / Math.max(1, blastProgress.total)) * 100}%` }}/>
                </div>
              </div>
            )}
            <Button onClick={blast} disabled={busy} data-testid="blast-confirm" className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded-full">
              {busy ? `Sending... ${blastProgress?.sent || 0}/${blastProgress?.total || 0}` : `Send to ${selectedIds.length}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR code modal */}
      <Dialog open={!!showQR} onOpenChange={v => !v && setShowQR(null)}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-sm text-center" data-testid="qr-dialog">
          <DialogHeader><DialogTitle>Reply QR Code</DialogTitle></DialogHeader>
          {showQR && (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">
                {showQR.name || "—"} · <span className="font-mono-alt">{showQR.phone}</span>
              </div>
              <div className="text-xs text-amber-400 truncate">{showQR.source_name}</div>
              <div className="bg-white rounded-lg p-4 inline-block">
                <img
                  data-testid="qr-image"
                  alt="QR"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent((window.location.origin) + "/reply/" + showQR.id)}`}
                  width="260" height="260"/>
              </div>
              <div className="text-[11px] text-slate-500 break-all">
                {window.location.origin}/reply/{showQR.id}
              </div>
              <Button onClick={() => window.print()} variant="secondary" className="bg-slate-800 hover:bg-slate-700 text-white rounded-full text-xs">
                Print QR
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
